import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaCalendarCheck, FaMapMarkerAlt } from 'react-icons/fa';
import { playerAPI } from '../../services/api';

const BRAND = { blue: '#043663', red: '#D52B1E' };

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const Section = styled.section`
  margin: 0;
  padding: 0 0 1.5rem;
  max-width: 960px;
  min-height: min(65vh, 560px);
  animation: ${fadeUp} 0.3s ease-out;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: ${BRAND.blue};
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 0.45rem 0.85rem;
  margin: 0 0 1.1rem 0;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  &:hover {
    border-color: rgba(4, 54, 99, 0.28);
    background: #f8fafc;
  }
`;

const HeaderBlock = styled.div`
  margin-bottom: 1.35rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.35rem 0;
  font-size: clamp(1.4rem, 3vw, 1.75rem);
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.03em;
`;

const SectionHint = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.5;
  max-width: 36rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 10px 24px rgba(15, 23, 42, 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: rgba(4, 54, 99, 0.25);
    box-shadow: 0 8px 22px rgba(4, 54, 99, 0.1);
    transform: translateY(-1px);
  }
`;

const CoverFrame = styled.div`
  background: ${(p) =>
    p.$placeholder
      ? 'linear-gradient(135deg, #043663 0%, #0a5a9e 55%, #032842 100%)'
      : '#0b1220'};
  min-height: ${(p) => (p.$placeholder ? '148px' : '0')};
`;

const CoverImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const Body = styled.div`
  padding: 0.95rem 1rem 1.15rem;
`;

const Name = styled.div`
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 0.4rem;
  letter-spacing: -0.02em;
`;

const Meta = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.5;
`;

const VenueRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.25rem;
`;

const BookingList = styled.ul`
  list-style: none;
  margin: 0 0 1.5rem 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BookingItem = styled(Link)`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  text-decoration: none;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  &:hover {
    border-color: rgba(4, 54, 99, 0.25);
  }
`;

const SubHeading = styled.h3`
  margin: 0 0 0.65rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
`;

const EmptyPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1.5rem;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px dashed rgba(4, 54, 99, 0.18);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  min-height: 280px;
  justify-content: center;
`;

const EmptyIcon = styled.div`
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: linear-gradient(145deg, #043663, #0a5a9e);
  color: #fff;
  font-size: 1.35rem;
  box-shadow: 0 8px 18px rgba(4, 54, 99, 0.25);
`;

const EmptyTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
`;

const EmptyBody = styled.p`
  margin: 0 0 1.25rem;
  max-width: 28rem;
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.55;
`;

const EmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  justify-content: center;
`;

const GhostBtn = styled.button`
  border: 1px solid #e2e8f0;
  background: #fff;
  color: ${BRAND.blue};
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.55rem 0.95rem;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    border-color: rgba(4, 54, 99, 0.3);
  }
`;

const PrimaryBtn = styled.button`
  border: none;
  background: ${BRAND.red};
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.55rem 0.95rem;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: #b91c1c;
  }
`;

const SkeletonCard = styled.div`
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #fff;
`;

const SkeletonBlock = styled.div`
  height: ${(p) => p.$h || '148px'};
  background: linear-gradient(110deg, #f1f5f9 8%, #e8eef5 18%, #f1f5f9 33%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s linear infinite;
  margin: ${(p) => p.$m || '0'};
  border-radius: ${(p) => p.$r || '0'};
`;

function coverSrc(event) {
  if (event.coverImageUrl) return event.coverImageUrl;
  if (event.hasCoverImage) return `/api/public/organised-events/${event.id}/cover-image`;
  return null;
}

export default function PlayerOrganisedTournamentsSection({ showBack = true }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: listRes, isLoading } = useQuery(
    ['playerOrganisedTournaments'],
    () => playerAPI.getOrganisedEvents({ eventType: 'tournament' }),
    { staleTime: 30 * 1000 }
  );
  const { data: bookingsRes } = useQuery(
    ['playerOrganisedBookings'],
    () => playerAPI.getOrganisedEventBookings(),
    { staleTime: 30 * 1000 }
  );

  const events = Array.isArray(listRes?.data) ? listRes.data : listRes?.data?.data || [];
  const bookings = Array.isArray(bookingsRes?.data) ? bookingsRes.data : bookingsRes?.data?.data || [];
  const confirmed = bookings.filter((b) => b.bookingStatus === 'confirmed');

  return (
    <Section>
      {showBack && (
        <BackBtn type="button" onClick={() => navigate('/player/tournaments')}>
          <FaArrowLeft size={12} aria-hidden />
          {t('playerOrganisedEvents.backToEvents', 'Back to Events')}
        </BackBtn>
      )}
      <HeaderBlock>
        <SectionTitle>{t('playerOrganisedEvents.tournamentsTitle', 'Tournaments')}</SectionTitle>
        <SectionHint>
          {t(
            'playerOrganisedEvents.tournamentsHint',
            'Book a spot at admin-organised tournaments.'
          )}
        </SectionHint>
      </HeaderBlock>

      {confirmed.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <SubHeading>{t('playerOrganisedEvents.myBookings', 'My bookings')}</SubHeading>
          <BookingList>
            {confirmed.map((b) => (
              <BookingItem key={b.bookingId} to={`/player/events/tournaments/${b.event.id}`}>
                <span>{b.event.name}</span>
                <span style={{ color: '#64748b', fontWeight: 500 }}>
                  {b.slot?.startsAt
                    ? new Date(b.slot.startsAt).toLocaleString()
                    : b.event.startsAt
                      ? new Date(b.event.startsAt).toLocaleDateString()
                      : ''}
                </span>
              </BookingItem>
            ))}
          </BookingList>
        </div>
      )}

      {isLoading ? (
        <Grid>
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i}>
              <SkeletonBlock $h="148px" />
              <div style={{ padding: '0.95rem 1rem 1.15rem' }}>
                <SkeletonBlock $h="1rem" $m="0 0 0.55rem" $r="6px" />
                <SkeletonBlock $h="0.7rem" $m="0 0 0.35rem" $r="6px" />
                <SkeletonBlock $h="0.7rem" $r="6px" />
              </div>
            </SkeletonCard>
          ))}
        </Grid>
      ) : events.length === 0 ? (
        <EmptyPanel>
          <EmptyIcon aria-hidden>
            <FaCalendarCheck />
          </EmptyIcon>
          <EmptyTitle>
            {t('playerOrganisedEvents.emptyTitle', 'No tournaments open yet')}
          </EmptyTitle>
          <EmptyBody>
            {t(
              'playerOrganisedEvents.emptyBody',
              'When HPS publishes a bookable tournament, it will show up here with venue, dates, and spots. In the meantime your season play lives under Leagues.'
            )}
          </EmptyBody>
          <EmptyActions>
            <GhostBtn type="button" onClick={() => navigate('/player/tournaments')}>
              {t('playerOrganisedEvents.backToEvents', 'Back to Events')}
            </GhostBtn>
            <PrimaryBtn
              type="button"
              onClick={() => navigate('/player/tournaments?area=leagues')}
            >
              {t('playerOrganisedEvents.goToLeagues', 'Go to Leagues')}
            </PrimaryBtn>
          </EmptyActions>
        </EmptyPanel>
      ) : (
        <Grid>
          {events.map((ev) => {
            const src = coverSrc(ev);
            return (
            <Card key={ev.id} to={`/player/events/tournaments/${ev.id}`}>
              {src ? (
                <CoverFrame>
                  <CoverImg src={src} alt={ev.name || ''} />
                </CoverFrame>
              ) : (
                <CoverFrame $placeholder />
              )}
              <Body>
                <Name>{ev.name}</Name>
                <Meta>
                  <VenueRow>
                    <FaMapMarkerAlt size={11} aria-hidden />
                    {ev.clubName || t('playerOrganisedEvents.venueTbd', 'Venue TBC')}
                  </VenueRow>
                  <div>
                    {Array.isArray(ev.slots) && ev.slots.length > 1
                      ? `${ev.slots.length} start times`
                      : ev.startsAt
                        ? new Date(ev.startsAt).toLocaleString()
                        : '—'}
                  </div>
                  <div>
                    {Number(ev.priceChf) <= 0
                      ? t('playerOrganisedEvents.free', 'Free')
                      : `CHF ${Number(ev.priceChf).toFixed(2)}`}
                    {' · '}
                    {ev.status === 'full'
                      ? t('playerOrganisedEvents.full', 'Full')
                      : t('playerOrganisedEvents.spotsLeft', '{{count}} spots left', {
                          count: ev.remainingSpots,
                        })}
                  </div>
                </Meta>
              </Body>
            </Card>
            );
          })}
        </Grid>
      )}
    </Section>
  );
}
